import { graphql } from '$contentful-gql/index';

export const sysFragment = graphql(/* GraphQL */ `
  fragment sys on Entry {
    sys {
      id
      publishedAt
    }
    __typename
  }
`);

export const assetFragment = graphql(/* GraphQL */ `
  fragment asset on Asset {
    __typename
    sys {
      id
    }
    title
    description
    contentType
    width
    height
    url
  }
`);

// TODO: enable this, time permitting
// ... on Page {
//   ...pathSegment
// }
export const pageUrlFragment = graphql(/* GraphQL */ `
  fragment pageUrl on Entry {
    ... on Url {
      ...url
    }
  }
`);

export const typeAssetFragment = graphql(/* GraphQL */ `
  fragment typeAsset on ContentTypeAsset {
    image(preview: $preview, locale: $language) {
      ...asset
    }
    imageUrl(locale: $language)
    video(preview: $preview, locale: $language) {
      ...asset
    }
    videoUrl(locale: $language)
    sizes(locale: $language)
    context(locale: $language)
    autoplay(locale: $language)
    controls(locale: $language)
    loop(locale: $language)
    muted(locale: $language)
    videoResolution(locale: $language)
  }
`);

export const textFragment = graphql(/* GraphQL */ `
  fragment text on Text {
    ...sys
    name(locale: $language)
    title(locale: $language)
    subtitle(locale: $language)
    caption(locale: $language)
    description(locale: $language)
    richText(locale: $language) {
      json
    }
  }
`);

export const pathSegmentFragment = graphql(/* GraphQL */ `
  fragment pathSegment on Page {
    ...sys
    name
    country
    language
    slug
    pageType
    path: slug
  }
`);

export const urlFragment = graphql(/* GraphQL */ `
  fragment url on Url {
    ...sys
    name(locale: $language)
    label(locale: $language)
    internalLink(preview: $preview) {
      ...pathSegment
    }
    externalLink(locale: $language)
    linkType(locale: $language)
    queryParameters(locale: $language)
    contactFormRole(locale: $language)
  }
`);

export const ctaFragment = graphql(/* GraphQL */ `
  fragment cta on Cta {
    ...sys
    name(locale: $language)
    template(locale: $language)
    url(preview: $preview) {
      ...url
    }
    icon(locale: $language)
    iconPosition(locale: $language)
    newTab(locale: $language)
  }
`);

export const mediaFragment = graphql(/* GraphQL */ `
  fragment media on Media {
    ...sys
    name(locale: $language)
    assetsCollection(skip: 0, limit: 4, preview: $preview) {
      total
      items {
        ...typeAsset
      }
    }
  }
`);

export const videoFragment = graphql(/* GraphQL */ `
  fragment video on Video {
    ...sys
    name(locale: $language)
    title(locale: $language)
    duration(locale: $language)
    vimeoUrl(locale: $language)
    autoplay(locale: $language)
    controls(locale: $language)
    loop(locale: $language)
    muted(locale: $language)
    type(locale: $language)
    posterCollection(skip: 0, limit: 3, preview: $preview) {
      total
      items {
        ...typeAsset
      }
    }
  }
`);

export const galleryItemFragment = graphql(/* GraphQL */ `
  fragment galleryItem on GalleryItem {
    ...sys
    name(locale: $language)
    itemType(locale: $language)
    assetsCollection(preview: $preview, limit: 5) {
      total
      items {
        ...typeAsset
      }
    }
    text(preview: $preview) {
      ...text
    }
  }
`);

export const officeFragment = graphql(/* GraphQL */ `
  fragment office on Office {
    ...sys
    name(locale: $language)
    title(locale: $language)
    streetAddressLine1(locale: $language)
    streetAddressLine2(locale: $language)
    addressLocality(locale: $language)
    addressRegion(locale: $language)
    postalCode(locale: $language)
    addressCountry(locale: $language)
    phone(locale: $language)
    email(locale: $language)
    ukCompanyNumber(locale: $language)
    googleMapsLink(locale: $language)
  }
`);

export const newsFragment = graphql(/* GraphQL */ `
  fragment news on News {
    ...sys
    date(locale: $language)
    type(locale: $language)
    title(locale: $language)
    subtitle(locale: $language)
    description(locale: $language)
    assetOverlay
    organizationLogo {
      ...asset
    }
    url(preview: $preview) {
      ...url
    }
    assetsCollection(skip: 0, limit: 4, preview: $preview) {
      total
      items {
        ...typeAsset
      }
    }
    linkedFrom(allowedLocales: [$language]) {
      pageCollection(skip: 0, limit: 1, preview: $preview) {
        total
        items {
          ...pathSegment
        }
      }
    }
  }
`);

export const newsQuery = graphql(/* GraphQL */ `
  query newsQuery(
    $skip: Int! = 0
    $limit: Int! = 50
    $preview: Boolean = false
    $language: String
    $filter: String
  ) {
    newsCollection(
      skip: $skip
      limit: $limit
      preview: $preview
      where: { type: $filter }
      order: [date_DESC]
    ) {
      total
      skip
      limit
      items {
        ...news
      }
    }
  }
`);

export const pageContentItemFragment = graphql(/* GraphQL */ `
  fragment pageContentItem on PageContentItem {
    ...hero
    ...list
    ...card
    ...carousel
    ...gallery
    ...timeline
    ...media
    ...video
    ...section
    ...faq
    ...html
  }
`);

export const newsArticleRichTextEntryFragment = graphql(/* GraphQL */ `
  fragment newsArticleRichTextEntry on Entry {
    ...sys
    ...pathSegment
    ...media
  }
`);

export const newsArticleRichTextLinksFragment = graphql(/* GraphQL */ `
  fragment newsArticleRichTextLinks on NewsArticleLinks {
    assets {
      hyperlink {
        ...asset
      }
      block {
        ...asset
      }
    }
    entries {
      inline {
        ...newsArticleRichTextEntry
      }
      hyperlink {
        ...newsArticleRichTextEntry
      }
      block {
        ...newsArticleRichTextEntry
      }
    }
  }
`);

export const newsArticleRichTextFragment = graphql(/* GraphQL */ `
  fragment newsArticleRichText on NewsArticle {
    json
    links {
      ...newsArticleRichTextLinks
    }
  }
`);

export const metaDataFragment = graphql(/* GraphQL */ `
  fragment metaData on MetaData {
    ...sys
    title(locale: $language)
    description(locale: $language)
    assetsCollection(skip: 0, limit: 3, preview: $preview) {
      total
      items {
        ...typeAsset
      }
    }
    context(locale: $language)
  }
`);

export const seoFragment = graphql(/* GraphQL */ `
  fragment seo on SeoSitemapRules {
    ...sys
    name(locale: $language)
    robotsNoFollow(locale: $language)
    robotsNoIndex(locale: $language)
    indexFrequency(locale: $language)
    priority(locale: $language)
  }
`);

export const newsPageQuery = graphql(/* GraphQL */ `
  query NewsPageQuery(
    $language: String
    $id: String!
    $preview: Boolean = false
  ) {
    page(id: $id, preview: $preview) {
      parentPage(preview: $preview) {
        ...pathSegment
      }
      structuredDataCollection(skip: 0, limit: 1, preview: $preview) {
        total
        items {
          ...news
        }
      }
    }
  }
`);
